import { type Color, WHITE, moveFrom, moveTo, moveToUci } from '@chessbut/engine';
import { useEffect, useMemo, useState } from 'react';
import { type SideBoardSpec, pieceImage } from '../variants/ui.ts';

interface SideBoardProps {
  spec: SideBoardSpec;
  orientation: Color;
  /** Legal moves right now (empty when it isn't the viewer's turn). */
  legalMoves: number[];
  showLegalMoves: boolean;
  onMove(uci: string): void;
}

/** A small second board (the Pit, the duel): tap a piece, then where it goes. */
export function SideBoard({ spec, orientation, legalMoves, showLegalMoves, onMove }: SideBoardProps) {
  const { cols, rows } = spec;
  const [selected, setSelected] = useState<number | null>(null);
  const [choice, setChoice] = useState<number[] | null>(null);

  const moves = useMemo(() => legalMoves.filter((m) => spec.isMove(m)), [legalMoves, spec]);
  const fromCell = (m: number) => spec.cellOf(moveFrom(m));
  const toCell = (m: number) => spec.cellOf(moveTo(m));

  useEffect(() => {
    setSelected(null);
    setChoice(null);
  }, [legalMoves]);

  const targets = new Set(selected === null ? [] : moves.filter((m) => fromCell(m) === selected).map(toCell));
  const movable = new Set(moves.map(fromCell));

  const tap = (cell: number) => {
    if (choice) return;
    if (selected !== null && targets.has(cell)) {
      const options = moves.filter((m) => fromCell(m) === selected && toCell(m) === cell);
      if (options.length === 1) onMove(moveToUci(options[0]));
      else setChoice(options);
      return;
    }
    setSelected(movable.has(cell) && selected !== cell ? cell : null);
  };

  // Display order: White at the bottom unless the board is flipped.
  const order: number[] = [];
  for (let y = 0; y < rows; y++) {
    for (let x = 0; x < cols; x++) {
      const r = orientation === WHITE ? rows - 1 - y : y;
      const f = orientation === WHITE ? x : cols - 1 - x;
      order.push(r * cols + f);
    }
  }

  return (
    <section className={`side-board ${spec.active ? 'is-active' : ''}`} aria-label={spec.title}>
      <header>
        <b>{spec.title}</b>
        {spec.hint && <span>{spec.hint}</span>}
      </header>
      <div className="side-grid" style={{ gridTemplateColumns: `repeat(${cols}, 1fr)`, aspectRatio: `${cols} / ${rows}` }}>
        {order.map((cell) => {
          const r = Math.floor(cell / cols);
          const f = cell % cols;
          const light = (r + f) % 2 === 1;
          const p = spec.cells[cell];
          const isTarget = targets.has(cell);
          return (
            <button
              key={cell}
              className={`side-sq ${light ? 'light' : 'dark'} ${cell === selected ? 'selected' : ''} ${movable.has(cell) ? 'can-move' : ''}`}
              aria-label={`${'abcdefgh'[f]}${r + 1}${p ? '' : ', empty'}${isTarget ? ', you can move here' : ''}`}
              onClick={() => tap(cell)}
            >
              {p ? <img src={pieceImage(p)} alt="" draggable={false} /> : null}
              {isTarget && showLegalMoves && <span className={p ? 'hint capture' : 'hint'} />}
            </button>
          );
        })}
      </div>
      {choice && (
        <div className="side-choice" role="dialog" aria-label="Choose">
          {choice.map((m) => (
            <button key={m} className="btn" onClick={() => onMove(moveToUci(m))}>
              {spec.optionLabel?.(m) ?? moveToUci(m)}
            </button>
          ))}
          <button className="btn ghost" onClick={() => setChoice(null)}>
            Cancel
          </button>
        </div>
      )}
    </section>
  );
}
